import { useCallback, useSyncExternalStore } from 'react';
import { brandedKey } from './brandedKey.js';

/**
 * Settings that belong to the *device*, not to the account.
 *
 * Everything in here answers a question only the phone in somebody's hand can
 * answer — may it buzz, may it keep the screen lit — so it is deliberately
 * not part of the account preferences the server stores. Signing in on a
 * tablet must not carry over the phone's answer, and using the app without an
 * account must not mean doing without these.
 *
 * The store is module level rather than a context, because the same
 * preference is read from screens that never share a parent (the settings
 * screen, the swipe screen, the match overlay). A tiny subscription keeps
 * them in step.
 */
export type DevicePrefName = 'haptics' | 'keepAwake';

/** On by default: both are the behaviour somebody would expect from an app. */
const DEFAULTS: Record<DevicePrefName, boolean> = {
  haptics: true,
  keepAwake: true,
};

const cache = new Map<DevicePrefName, boolean>();
const listeners = new Map<DevicePrefName, Set<() => void>>();

function storageKey(name: DevicePrefName): string {
  return brandedKey(`pref.${name}`);
}

/** Stored as `on`/`off` rather than JSON: it is read in a hot path and a
 *  mangled value has to fall back to the default, not throw. */
export function readDevicePref(name: DevicePrefName): boolean {
  const cached = cache.get(name);
  if (cached !== undefined) return cached;

  let value = DEFAULTS[name];
  try {
    const raw = window.localStorage.getItem(storageKey(name));
    if (raw === 'on') value = true;
    else if (raw === 'off') value = false;
  } catch {
    // Private mode or blocked storage: the default is a fine answer.
  }
  cache.set(name, value);
  return value;
}

export function writeDevicePref(name: DevicePrefName, value: boolean): void {
  cache.set(name, value);
  try {
    window.localStorage.setItem(storageKey(name), value ? 'on' : 'off');
  } catch {
    // The choice then lasts for this tab only, which still beats refusing it.
  }
  for (const listener of listeners.get(name) ?? []) listener();
}

export function subscribeDevicePref(name: DevicePrefName, listener: () => void): () => void {
  const set = listeners.get(name) ?? new Set<() => void>();
  set.add(listener);
  listeners.set(name, set);
  return () => set.delete(listener);
}

/** Only for tests: drops the in-memory copy so a fresh storage state is read. */
export function resetDevicePrefs(): void {
  cache.clear();
}

/** The preference plus its setter, kept in sync across every mounted reader. */
export function useDevicePref(name: DevicePrefName): [boolean, (value: boolean) => void] {
  const value = useSyncExternalStore(
    useCallback((listener: () => void) => subscribeDevicePref(name, listener), [name]),
    useCallback(() => readDevicePref(name), [name]),
    // Server snapshot: there is no server rendering here, but the third
    // argument is what keeps this hook honest if one is ever added.
    useCallback(() => DEFAULTS[name], [name]),
  );
  const set = useCallback((next: boolean) => writeDevicePref(name, next), [name]);
  return [value, set];
}
